// People — the org directory with live presence (Layer B1), its own top-level
// page between Meetings and Calls. Each row offers the two ways to reach
// someone: a message (opens a Discussion) and a call. The All/Online toggle
// narrows to who's around. Rows come from the server once; the status dots are
// live from the presence store (§9.3), so a transition repaints one row.

import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { api, DirectoryEntry } from '../api';
import { ensureReady as ensureChatReady, openConversation, startDM } from '../chat/chatClient';
import { useShell } from '../components/AppShell';
import { pushToast } from '../store/callStore';
import { PresenceStatus, statusOf, usePresenceStore } from '../store/presenceStore';
import { Avatar } from '../ui/Avatar';
import { IconCall, IconChat, IconSearch } from '../ui/icons';

type LoadState = 'loading' | 'ready' | 'error';

const CALL_HINT: Record<PresenceStatus, string> = {
  online: 'Start an audio call',
  in_call: 'They are already on a call — it won’t ring',
  dnd: 'Do not disturb is on — it won’t ring',
  offline: 'They are offline — it may not ring',
};

export function PeopleDirectoryPage() {
  const { me } = useShell();
  const navigate = useNavigate();

  const [people, setPeople] = useState<DirectoryEntry[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [q, setQ] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);

  const onQ = useCallback((e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value), []);
  const showAll = useCallback(() => setOnlineOnly(false), []);
  const showOnline = useCallback(() => setOnlineOnly(true), []);

  useEffect(() => {
    api
      .directory()
      .then((list) => {
        setPeople(list);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  const onCall = useCallback(
    async (person: DirectoryEntry) => {
      try {
        const r = await api.startCall([{ id: person.id, name: person.name }], 'audio');
        navigate(`/call/${r.call.id}`);
      } catch (e) {
        pushToast(e instanceof Error ? e.message : 'Could not start the call', 'error');
      }
    },
    [navigate],
  );

  const onMessage = useCallback(
    async (person: DirectoryEntry) => {
      try {
        if (me) await ensureChatReady(me.id);
        const id = await startDM(person.id);
        await openConversation(id);
        navigate('/chat');
      } catch (e) {
        pushToast(e instanceof Error ? e.message : 'Could not start the chat', 'warning');
      }
    },
    [me, navigate],
  );

  if (!me) return <Navigate to="/" replace />;

  return (
    <div className="bz-shell-col">
      <header className="bz-people-header">
        <div className="bz-people-head-left">
          <h1>People</h1>
        </div>
        <div className="bz-seg" role="tablist" aria-label="Filter">
          <button
            type="button"
            role="tab"
            aria-selected={!onlineOnly}
            className={onlineOnly ? 'bz-seg-item' : 'bz-seg-item bz-seg-item-on'}
            onClick={showAll}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={onlineOnly}
            className={onlineOnly ? 'bz-seg-item bz-seg-item-on' : 'bz-seg-item'}
            onClick={showOnline}
          >
            Online
          </button>
        </div>
      </header>

      <div className="bz-people-toolbar">
        <div className="bz-people-search">
          <IconSearch size={16} />
          <input
            type="search"
            placeholder="Search people"
            value={q}
            onChange={onQ}
            aria-label="Search people"
          />
        </div>
      </div>

      <Directory
        rows={people}
        state={state}
        q={q}
        onlineOnly={onlineOnly}
        onCall={onCall}
        onMessage={onMessage}
        canCall={me.callsEnabled}
      />
    </div>
  );
}

function Directory({
  rows,
  state,
  q,
  onlineOnly,
  onCall,
  onMessage,
  canCall,
}: {
  rows: DirectoryEntry[];
  state: LoadState;
  q: string;
  onlineOnly: boolean;
  onCall: (p: DirectoryEntry) => void;
  onMessage: (p: DirectoryEntry) => void;
  canCall: boolean;
}) {
  const presence = usePresenceStore((s) => s);
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (ql && !r.name.toLowerCase().includes(ql) && !(r.email ?? '').toLowerCase().includes(ql)) {
        return false;
      }
      if (onlineOnly) {
        const s = presence.connected ? statusOf(presence, r.id) : r.status;
        if (s === 'offline') return false;
      }
      return true;
    });
  }, [rows, q, onlineOnly, presence]);

  if (state === 'loading') return <p className="bz-people-note">Loading…</p>;
  if (state === 'error') {
    return <p className="bz-people-note bz-error-text">Could not load the directory.</p>;
  }
  if (filtered.length === 0) {
    return (
      <p className="bz-people-note">
        {q ? 'No one matches your search.' : onlineOnly ? 'No one online right now.' : 'No one here yet.'}
      </p>
    );
  }

  return (
    <div className="bz-people-rows">
      {filtered.map((p) => (
        <PersonRow key={p.id} person={p} onCall={onCall} onMessage={onMessage} canCall={canCall} />
      ))}
    </div>
  );
}

const PersonRow = memo(function PersonRow({
  person,
  onCall,
  onMessage,
  canCall,
}: {
  person: DirectoryEntry;
  onCall: (p: DirectoryEntry) => void;
  onMessage: (p: DirectoryEntry) => void;
  canCall: boolean;
}) {
  const status = usePresenceStore((s) => (s.connected ? statusOf(s, person.id) : person.status));
  const call = useCallback(() => onCall(person), [onCall, person]);
  const message = useCallback(() => onMessage(person), [onMessage, person]);

  return (
    <div className="bz-person-row">
      <span className="bz-person-avatar" data-status={status}>
        <Avatar name={person.name} identity={person.id} size={40} photoUrl={person.avatar} />
        <span className={`bz-person-dot bz-person-dot-${status}`} aria-hidden="true" />
      </span>
      <div className="bz-person-body">
        <div className="bz-person-name">
          {person.name}
          {person.self && <span className="bz-person-you">You</span>}
        </div>
        {person.email && <div className="bz-person-email">{person.email}</div>}
      </div>
      {!person.self && (
        <div className="bz-person-actions">
          <button
            type="button"
            className="bz-person-iconbtn"
            onClick={message}
            aria-label={`Message ${person.name}`}
            title={`Message ${person.name}`}
          >
            <IconChat size={17} />
          </button>
          {canCall && (
            <button
              type="button"
              className="bz-person-iconbtn"
              onClick={call}
              aria-label={`Call ${person.name}`}
              title={CALL_HINT[status]}
            >
              <IconCall size={17} />
            </button>
          )}
        </div>
      )}
    </div>
  );
});
